import { AuthConstantS, AuthErrorS } from '@/features/Auth/outbound/share';
import { SnsErrorS, type SnsTypeS } from '@/features/Sns/outbound/share';
import { MCO_CONNECT_FAQ_PATH, MCO_DOMAIN } from '@/libs/constant';
import { AT, T } from '@/libs/translation';
import { ButtonSnsIcon, Link, Typo } from '@/ui/component/atom';
import { Header, Layout } from '@/ui/component/cell';
import { getSharedData, type SharedData } from '@/ui/util';
import { filter, keys, map, pipe, toArray } from '@fxts/core';
import { HydratedEvent } from '@rune-ts/server';
import { html, type Html, on, Page, View } from 'rune-ts';
import { PostMessage } from '../../../util/postMessage';
import { LogoView } from '../../view/Logo/Logo';
import { makeErrorPopup } from '../../view/Popup/makeErrorPopup';
import auth_klass from '../Common/Auth.module.scss';
import klass from './ConnectToSnsPage.module.scss';

export interface ConnectToSnsPageData {
  sns: SnsTypeS.SnsType[];
  error_code?: 'SNS_004' | 'SNS_006' | 'USER_003' | 'SNS_010';
  sns_request_referer?: string;
}

export class ConnectToSnsPageBodyView extends View<ConnectToSnsPageData> {
  override template() {
    const { theme, client_id } = getSharedData(this);
    const { sns } = this.data;

    return html` <div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <div class="${klass.guide_container}">
            ${new Typo({ text: T('auth.connect.sns_user_guide') }, { weight: 'bold' })}
            ${new Typo({ text: html.preventEscape(T('auth.connect.sns_user_guide_description')) }, { typo: 'p2' })}
          </div>
          <div class="${klass.connect}">
            ${new Typo({ text: T('auth.connect.sns_connect') }, { color: 'gray_80' })}
            <div class="${klass.button_container}">
              ${pipe(
                AuthConstantS.USER_LOGIN_SNS_TYPE_MAPPER,
                keys,
                filter((type) => sns.includes(type)),
                map((type) => {
                  return new Link(
                    {
                      label: new ButtonSnsIcon(
                        {
                          type,
                          label: new Typo({
                            text: T('auth.connect.verify', { sns_type: T(`common.${type}`, { context: 'suffix' }) }),
                          }),
                        },
                        { active: true, button_type: 'fullsize', hasRightSpace: true },
                      ),
                      href: `/oauth/request/${type.toLowerCase()}?type=verify&client_id=${client_id}`,
                    },
                    {
                      klass: klass.button,
                    },
                  );
                }),
                toArray,
              )}
            </div>
            <div class="${klass.link_container}">
              ${new Link(
                {
                  label: new Typo({ text: T('auth.connect.reason') }, { typo: 'p2', color: 'gray_80', block: true }),
                  href: `${MCO_DOMAIN[theme]}${MCO_CONNECT_FAQ_PATH[theme]}`,
                },
                { target: '_blank', klass: 'faq' },
              )}
              <span class="${klass.divider}"></span>
              ${new Link({
                label: new Typo({ text: T('auth.signup.go_login') }, { typo: 'p2', color: 'gray_80', block: true }),
                href: `/${AT.language}/login?client_id=${client_id}`,
              })}
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  override onRender() {
    this.delegate('click', '.faq', this.linkClickHandler);
  }

  linkClickHandler(e: Event & { originalEvent: Event }) {
    const { app_info } = getSharedData(this);

    if (app_info?.name === 'MarppleShop') {
      e.originalEvent.preventDefault();
      PostMessage.goEmptyScreen({ uri: `${MCO_DOMAIN[app_info?.name]}${MCO_CONNECT_FAQ_PATH[app_info?.name]}` });
    }
  }
}

export class ConnectToSnsPage extends Page<ConnectToSnsPageData> {
  layout: Layout;

  constructor(data: ConnectToSnsPageData, sharedData: SharedData) {
    super(data, sharedData);

    this.layout = new Layout(
      {
        view: new ConnectToSnsPageBodyView(this.data),
      },
      {
        fullwidth: true,
        header: sharedData.device === 'mo' ? new Header({}) : undefined,
      },
    );
  }

  protected override template(): Html {
    return html` <div>${this.layout}</div> `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      if (this.data.sns_request_referer) {
        location.replace(this.data.sns_request_referer);
      } else {
        history.back();
      }
    });
  }

  @on(HydratedEvent)
  async onHydrated(_: HydratedEvent) {
    const { error_code } = this.data;
    if (error_code) {
      const error = (SnsErrorS[error_code] ?? AuthErrorS[error_code] ?? (() => ({})))();
      const res = await makeErrorPopup(error, {
        ...error?.json,
        ok: error_code === 'SNS_010' ? T('common.ok') : T('auth.signup.go_login'),
        cancel: error_code === 'SNS_010' ? undefined : T('common.ok'),
      });

      if (res === 'confirm' || error_code === 'SNS_010') {
        const { client_id } = getSharedData(this);
        location.href = `/${AT.language}/login?client_id=${client_id}`;
      }
    }
  }
}
